<!-- Profile → Settings: the time zone dates and times are shown in (restored 2026-10-13). The
     same store as the Program's menu (selectedTimeZones.js): a choice here goes first in the
     account's selectedTimeZones, so the Program, the /2026 pages, the banner and the Events tab
     all follow it. Shown: the preferred zone (the account's last choice, else this browser's, else
     the browser's own). The menu (TimeZoneMenu, 2026-10-14) offers the usual zones in full
     ("Australia/Sydney"), then every other zone by region. -->
<template>
  <div class="section">
    <div class="title">Time Zone</div>
    <div class="row card" :class="{isSaving}">
      <TimeZoneMenu
        :options="suggested"
        :selected="selected"
        :label="`Time zone: ${getLabel(selected)}`"
        is-full-name
        @pick="pick">
        <span class="current">{{ getLabel(selected) }}</span>
        <span class="chevron" aria-hidden="true">⌄</span>
      </TimeZoneMenu>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watchEffect} from "vue";
import history from "@/assets/history.json";
import useUserSafe from "@/composables/useUserSafe";
import TimeZoneMenu from "@/components/TimeZoneMenu.vue";
import {getLabel} from "@/components/timeZoneLevels";
import {
  browserTimeZone, getPreferredTimeZone, getSelectedTimeZones, saveSelectedTimeZone,
  setStoredTimeZone
} from "@/composables/selectedTimeZones";

const EVENT_ZONE = history.years[history.latestYear];

const {user} = useUserSafe();
const isSaving = ref(false);
const selected = ref("");
const suggested = computed(() => {
  const zones = [selected.value, browserTimeZone, EVENT_ZONE, ...getSelectedTimeZones(user.value)];
  return [...new Set(zones.filter(Boolean))];
});

watchEffect(() => {
  if (!isSaving.value) selected.value = getPreferredTimeZone(user.value);
});

async function pick(zone) {
  if (isSaving.value || zone === selected.value) return;
  selected.value = zone;
  setStoredTimeZone(zone);
  isSaving.value = true;
  await saveSelectedTimeZone(user.value, zone);
  isSaving.value = false;
}
</script>

<style scoped>
.section {
  margin-top: 8px;
  margin-bottom: 1.75rem;
}
.title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(0, 0, 0, 0.35);
  margin: 6px 0 5px 0;
}
/* the menu as the dialog's white cards, whatever the page's colour scheme */
.row {
  display: grid;
  align-items: center;
  border-radius: 12px;
  padding: 3px 8px;
  --menu-text: rgba(0, 0, 0, 0.8);
  --menu-background: white;
  --menu-border: rgba(0, 0, 0, 0.15);
  --menu-shadow: rgba(0, 0, 0, 0.18);
  --menu-highlight: rgba(0, 0, 0, 0.07);
  --menu-focus: rgba(0, 0, 0, 0.5);
}
.card {
  background: white;
  border-radius: 12px;
}
/* a plain row, as the <select> was: global.css's button look reset */
.row :deep(.trigger) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 24px;
  margin: 0;
  padding: 0;
  font: inherit;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.75);
  text-align: left;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  cursor: pointer;
}
.row :deep(.trigger:focus-visible) {
  outline: 2px solid rgba(0, 0, 0, 0.6);
  outline-offset: 2px;
  border-radius: 4px;
}
.current {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chevron {
  margin-left: 8px;
  transform: translateY(-0.2em);
  color: rgba(0, 0, 0, 0.45);
}
.isSaving {
  pointer-events: none;
  opacity: 0.6;
}
</style>
